import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    calculateHealthRatioFromLtv,
    calculateLiquidationPrice,
    calculateLtvFromLeverage,
    getSimpleInterest,
    LoopWindParams,
    useLoopscaleWalletContext,
    useLoopWindTransaction,
    LoopExpanded,
    LoopQuoteExpanded,
    AbfLoanExpanded,
    getAssetData,
    isWhirlpoolLoop,
    useUnderlyingTokenInfo,
    isPrincipalTokenWind,
    AbfTransactionDetails,
    useTokenListContext,
    useFillStrategyOfferTransaction,
    CreateLoanTxnParams,
    useLoopWindRefetch,
    useOraclePrices,
    isLoopCollateralWindDisabled,
    isLoopPrincipalWindDisabled
} from "@bridgesplit/abf-react";
import {
    bsMath,
    calculatePercentChange,
    decimalsToBps,
    LOADING_ERROR,
    MISSING_PARAM_ERROR,
    MISSING_WALLET_ERROR,
    Result,
    lamportsToUiAmount,
    parseErrorFromOptions,
    uiAmountToLamports,
    createAnalyticsInfo,
    AnalyticsTransactionType,
    LoopAnalyticsInfo
} from "@bridgesplit/utils";
import {
    getDurationInSeconds,
    LoanCreationParams,
    getDurationIndex,
    OracleQuote,
    TokenListMetadata
} from "@bridgesplit/abf-sdk";
import { TrackTransactionEvent } from "app/types";
import { trackSubmitEarnVault, trackSubmitMarketBorrowOrder, usePermissionedLoanCheck } from "app/hooks";
import { SwapInstructionsResponse } from "@loopscale/pandora-types";
import { getCpiFromSwapStep, getSwapInstructionSteps, mergeRouteInstructionSteps } from "app/utils/swapInstructions";

import { DEFAULT_SLIPPAGE } from "../../common";
import { useLoopContext } from "../LoopContext";
import { allTransactionsSucceeded, useTransactionSender } from "../../transactions";
import { useWindContext } from "./WindContext";
import { DEFAULT_WIND_FORM } from "./constants";
import { ExecutionDetailsLabels, LoopExecutionDetails } from "../execution-detail/types";
import { getExecutionPriceUsdPerBoughtFromQuote } from "../execution-detail/utils";
import { WindForm, ManualLoopMode } from "./types";
import type { LoopWinder } from "./winders/types";
import { getStaleOracleSimulationMessage } from "../../transactions/simulationErrors";

type WindSimulationBalanceChangesParams = {
    loopExpanded: LoopExpanded | undefined;
    form: WindForm;
    externalQuote: SwapInstructionsResponse | undefined;
    quote: LoopQuoteExpanded | null | undefined;
    leveragedCollateralAmountToDeposit: number | undefined;
    loopWinder: LoopWinder;
    activeWallet: string | undefined;
    getMetadata: ReturnType<typeof useTokenListContext>["getMetadata"];
    skip: boolean;
};

export function useLoopSetWindTokenAmount() {
    const { setForm } = useWindContext();
    return useCallback(
        (amount: number | undefined) => setForm((prev) => ({ ...prev, inputAmount: amount })),
        [setForm]
    );
}

const IRRECOVERABLE_SIMULATION_ERROR_MESSAGE = "Transaction may fail. Please try manual looping";
export const DEFAULT_SIMULATION_ERROR_MESSAGE =
    "Transaction may fail. If this problem persists, please try manual looping.";
const SLIPPAGE_ERROR_MESSAGE = "Transaction may fail. Try increasing slippage.";

const WIND_ERRORS = {
    "6001": SLIPPAGE_ERROR_MESSAGE,
    "encoding overruns uint8array": IRRECOVERABLE_SIMULATION_ERROR_MESSAGE
};

export enum WindSimulationErrorType {
    Slippage = "slippage",
    BorrowCap = "borrow_cap",
    StaleOracle = "stale_oracle",
    Unknown = "unknown"
}

export type WindSimulationError = {
    type: WindSimulationErrorType;
    message: string;
    showManualLoopLink: boolean;
};

export function parseWindSimulationError(rawError: string | undefined): WindSimulationError | undefined {
    if (!rawError) return undefined;

    const staleOracleMessage = getStaleOracleSimulationMessage([rawError]);
    if (staleOracleMessage) {
        return {
            type: WindSimulationErrorType.StaleOracle,
            message: staleOracleMessage,
            showManualLoopLink: false
        };
    }

    const parsed = parseErrorFromOptions(rawError, WIND_ERRORS) ?? DEFAULT_SIMULATION_ERROR_MESSAGE;
    return {
        type: parsed === SLIPPAGE_ERROR_MESSAGE ? WindSimulationErrorType.Slippage : WindSimulationErrorType.Unknown,
        message: parsed,
        showManualLoopLink: parsed !== DEFAULT_SIMULATION_ERROR_MESSAGE
    };
}
export function useLoopWind() {
    const { loopExpanded } = useLoopContext();
    const { form, setForm, externalQuote, quote, leveragedCollateralAmountToDeposit, LoopWinder } = useWindContext();

    const loanCreationParams = LoopWinder.getLoanCreationParams({
        loopExpanded,
        form
    });

    return useLoopWindCallback({
        loopExpanded,
        externalQuote,
        bestQuoteLendingStrategyAddress: quote?.bestQuote.lendingStrategyAddress,
        bestQuoteApy: quote?.bestQuote.apy,
        bestQuoteLqt: quote?.bestQuote.liquidationThreshold,
        principalAmountRequested: quote?.principalAmount,
        collateralAmountToDeposit: leveragedCollateralAmountToDeposit,
        multiplier: form.multiplier,
        percentSlippageDecimals: form.percentSlippageDecimals,
        loanCreationParams,
        durationIndex: getDurationIndex(form.strategyDuration),
        loanExpanded: undefined,
        onSuccess: () => setForm({ ...DEFAULT_WIND_FORM, inputToken: getDefaultWindInputToken(loopExpanded) }) // Form reset on success
    });
}

function getDefaultWindInputToken(loopExpanded: LoopExpanded | undefined) {
    if (!loopExpanded) return undefined;

    const principalMint = loopExpanded.principalToken.mint;
    const collateralMint = loopExpanded.collateralToken.mint;
    if (principalMint === collateralMint) return loopExpanded.inputToken;

    if (isLoopCollateralWindDisabled(loopExpanded.loopVault)) return loopExpanded.principalToken;
    if (isLoopPrincipalWindDisabled(loopExpanded.loopVault)) return loopExpanded.collateralToken;

    return loopExpanded.inputToken;
}

/**
 * Determines the sell token for GetAssetButton based on loop configuration.
 * If the collateral token matches the input token, returns the principal token.
 * Otherwise, returns the collateral token.
 */
export function getSellTokenForGetAssetButton(
    loopExpanded: LoopExpanded | undefined,
    inputToken: TokenListMetadata | undefined
): TokenListMetadata | undefined {
    if (!loopExpanded) return undefined;

    if (loopExpanded.collateralToken.mint === inputToken?.mint) {
        return loopExpanded.principalToken;
    }
    return loopExpanded.collateralToken;
}

const BORROW_ERRORS = {
    "Rate limit": "You're starting loans too quickly - please wait a few seconds and try again",
    "Offer is no longer available": "Quote is no longer available",
    "Borrow cap": "Your request exceeds the available borrow capacity for this market",
    "You can only borrow": "Your quote exceeds the global borrow limit per loan",
    "You have insufficient collateral": "Your quote exceeds your borrow limit"
};

export function useManualWindBorrow() {
    const send = useTransactionSender();
    const fillOrder = useFillStrategyOfferTransaction();
    const refetch = useLoopWindRefetch();
    const { loopExpanded } = useLoopContext();
    const { form, setForm, bestBorrowQuote, quote } = useWindContext();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const { loanNonce } = usePermissionedLoanCheck(loopExpanded?.principalToken, loopExpanded?.collateralToken);

    const durationIndex = getDurationIndex(form.strategyDuration);

    return async () => {
        if (
            !loopExpanded ||
            !form.principalAmount ||
            !form.collateralAmount ||
            !bestBorrowQuote ||
            !activeWallet ||
            !form.strategyDuration ||
            durationIndex === undefined
        )
            return Result.errFromMessage(LOADING_ERROR);

        const collateralToken = loopExpanded.collateralToken;
        const principalToken = loopExpanded.principalToken;

        const assetData = getAssetData(collateralToken, collateralToken.mint);

        if (!assetData) return Result.errFromMessage("Invalid Collateral Asset");

        const params: CreateLoanTxnParams = {
            borrower: activeWallet,
            depositCollateral: [
                {
                    collateralAmount: uiAmountToLamports(form.collateralAmount, collateralToken.decimals),
                    collateralAssetData: assetData
                }
            ],
            principalRequested: [
                {
                    ledgerIndex: 0,
                    principalAmount: uiAmountToLamports(form.principalAmount, principalToken.decimals),
                    principalMint: principalToken.mint,
                    strategy: bestBorrowQuote.lendingStrategyAddress,
                    durationIndex,
                    expectedLoanValues: {
                        expectedApy: decimalsToBps(bestBorrowQuote.apy),
                        expectedLqt: [decimalsToBps(bestBorrowQuote.liquidationThreshold), 0, 0, 0, 0]
                    }
                }
            ],
            assetIndexGuidance: [],
            loanNonce,
            isLoop: true
        };

        const fillOrderWithRefetch: typeof fillOrder = {
            ...fillOrder,
            sendOptions: {
                ...fillOrder.sendOptions,
                refetch: () => {
                    fillOrder.sendOptions.refetch?.();
                    refetch();
                }
            }
        };

        return await send(fillOrderWithRefetch, params, {
            alerter: { generationEstimateSeconds: 8, showProgress: true },
            messageOverrides: { errorMessageMap: BORROW_ERRORS },
            sendOptions: {
                onSuccess: () =>
                    setForm((prev) => ({
                        ...prev,
                        principalAmount: undefined,
                        collateralAmount: undefined,
                        manualMode: ManualLoopMode.InputCollateral,
                        ltvMultiplier: 0
                    }))
            },
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitMarketBorrowOrder,
                params: trackSubmitMarketBorrowOrder({
                    params,
                    duration: bestBorrowQuote,
                    source: "manual-loop"
                })
            }
        });
    };
}

export function useLoopHealth() {
    const { quote } = useWindContext();
    const { loopExpanded } = useLoopContext();

    return useMemo(() => {
        if (!quote || !loopExpanded) return undefined;
        const { bestQuote, leverageMultiplier } = quote;

        const ltv = calculateLtvFromLeverage(leverageMultiplier);
        const healthRatio = calculateHealthRatioFromLtv(ltv, bestQuote.liquidationThreshold);

        return { ltv, healthRatio, strategyLtv: bestQuote.ltv, strategyLqt: bestQuote.liquidationThreshold };
    }, [loopExpanded, quote]);
}

export function useManualLoopHealth() {
    const { form, quote } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const principalMetadata = loopExpanded?.principalToken;
    const collateralMetadata = loopExpanded?.collateralToken;
    const { getOracle } = useOraclePrices([collateralMetadata?.mint, principalMetadata?.mint]);

    const collateralOracle = getOracle(collateralMetadata?.mint);
    const principalOracle = getOracle(principalMetadata?.mint);

    const bestQuote = quote?.bestQuote;

    if (!form.collateralAmount || !form.principalAmount || !bestQuote || !collateralOracle || !principalOracle)
        return undefined;

    const collateralAmountUsd = collateralOracle.getUsdAmount(form.collateralAmount, collateralMetadata?.decimals);
    const principalAmountUsd = principalOracle.getUsdAmount(form.principalAmount, principalMetadata?.decimals);
    const ltv = principalAmountUsd / collateralAmountUsd;
    const healthRatio = calculateHealthRatioFromLtv(ltv, bestQuote.liquidationThreshold);

    return {
        ltv,
        healthRatio,
        strategyLtv: bestQuote.ltv,
        strategyLqt: bestQuote.liquidationThreshold
    };
}

export function useLoopLiquidationPrice() {
    const { quote } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const { oraclePrices } = useUnderlyingTokenInfo(
        loopExpanded?.principalToken.mint && loopExpanded?.collateralToken.mint
            ? [loopExpanded.principalToken.mint, loopExpanded.collateralToken.mint]
            : []
    );

    return useMemo(() => {
        if (!quote || !loopExpanded)
            return {
                liquidationPrice: 0,
                ltv: undefined,
                currentPrice: 0,
                percentChange: 0
            };

        const { bestQuote, leveragedCollateralAmount, principalAmount } = quote;

        const totalInterest = getSimpleInterest({
            principal: principalAmount,
            apy: bestQuote.apy,
            loanDuration: getDurationInSeconds(bestQuote),
            subtractPrincipal: true
        });

        const liquidationPrice = calculateLiquidationPrice(
            bsMath.tokenAdd(principalAmount, totalInterest, loopExpanded.collateralToken.decimals),
            leveragedCollateralAmount,
            bestQuote.liquidationThreshold
        );

        const currentPrice = bsMath.div(
            oraclePrices?.get(loopExpanded.collateralToken.mint)?.usdPrice,
            oraclePrices?.get(loopExpanded.principalToken.mint)?.usdPrice
        );
        const percentChange = calculatePercentChange(currentPrice, liquidationPrice);

        return { liquidationPrice, ltv: bestQuote.ltv, currentPrice, percentChange };
    }, [loopExpanded, quote, oraclePrices]);
}

export function useLoopWindCallback({
    // fix this to use an optional existing loan and use all correct deltas and params generically
    loopExpanded,
    externalQuote,
    collateralAmountToDeposit,
    multiplier,
    percentSlippageDecimals,
    loanCreationParams,
    onSuccess,
    bestQuoteLendingStrategyAddress,
    bestQuoteApy,
    bestQuoteLqt,
    principalAmountRequested,
    loanExpanded,
    durationIndex
}: {
    loopExpanded: LoopExpanded | undefined;
    externalQuote: SwapInstructionsResponse | undefined; // used for luts
    collateralAmountToDeposit: number | undefined;
    multiplier: number | undefined;
    percentSlippageDecimals: number | undefined;
    loanCreationParams: LoanCreationParams | undefined;
    onSuccess?: () => void;
    bestQuoteLendingStrategyAddress: string | undefined;
    bestQuoteApy: number | undefined;
    bestQuoteLqt: number | undefined;
    principalAmountRequested: number | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    durationIndex: number | undefined;
}) {
    const wind = useLoopWindTransaction(); // The original transaction hook
    const send = useTransactionSender();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const { getMetadata } = useTokenListContext();
    const principalMint = loopExpanded?.principalToken.mint;

    return useCallback(
        async ({
            collateralMint,
            inputMint
        }: {
            collateralMint: string | undefined;
            inputMint: string | undefined;
        }) => {
            if (!loopExpanded) return Result.errFromMessage(LOADING_ERROR);

            if (!activeWallet) return Result.errFromMessage(MISSING_WALLET_ERROR);

            // Merge every swap-route step into a single instruction stream and inline it into
            // flash_borrow.cpiIxs. Any multi-hop route (e.g. Jupiter → GLAM) runs as one wind tx
            // instead of splitting into a follow-up swap_collateral step. Setup ixs still ride
            // their own tx via unifySetup: false + splitAtaCreates: true, so the on-wire shape is
            // [setup, flash_borrow_main] (plus an optional oracle_crank BE prepends when stale).
            const swapSteps = getSwapInstructionSteps(externalQuote);
            const mergedSwapStep = mergeRouteInstructionSteps(swapSteps);
            const targetCollateralMint = mergedSwapStep?.outputMint ?? collateralMint;
            const finalSwapOutputLamports = mergedSwapStep?.minOutputAmount ?? mergedSwapStep?.inputAmount;

            if (!externalQuote || !multiplier || !inputMint || !targetCollateralMint || !mergedSwapStep)
                return Result.errFromMessage(MISSING_PARAM_ERROR);

            const targetCollateralMetadata = getMetadata(targetCollateralMint) ?? loopExpanded.collateralToken;
            const assetData = getAssetData(targetCollateralMetadata, targetCollateralMint);
            if (!assetData) return Result.errFromMessage(MISSING_PARAM_ERROR);
            // Winder returns (userInitialCollateral + swap.minOutputAmount) already summed — same
            // formula BE's DepositCollateral ix expects (moves exactly this many tokens from the
            // user's ATA into the loan). Previously we were dropping the initial-collateral half
            // by using only the swap output, which orphaned the user's deposit in their ATA.
            const collateralAmountForLoan = collateralAmountToDeposit ?? 0;

            const { cpiIxs, cpiLuts, cpiSigners, setupIxs, setupSigners } = getCpiFromSwapStep(mergedSwapStep, {
                flattenSetup: false
            });

            const params: LoopWindParams = {
                loopExpanded,
                depositMint: inputMint,
                depositCollateral: [
                    {
                        collateralAmount: collateralAmountForLoan,
                        collateralAssetData: assetData,
                        loanCreationParams: loanCreationParams
                    }
                ],
                // Keep pandora's setupIxs as their own tx so Jito bundle detection picks them up
                // (setup + main = 2 txs). Paired with optionalParams.splitAtaCreates: true on the
                // pandora request that produced this quote.
                unifySetup: false,
                leverageMultiplier: multiplier - (percentSlippageDecimals ?? DEFAULT_SLIPPAGE),
                principalRequested: [
                    {
                        ledgerIndex: 0,
                        principalAmount: principalAmountRequested ?? 0,
                        principalMint: principalMint ?? "",
                        strategy: bestQuoteLendingStrategyAddress ?? "",
                        durationIndex: durationIndex ?? 0,
                        expectedLoanValues: {
                            expectedApy: decimalsToBps(bestQuoteApy),
                            expectedLqt: [decimalsToBps(bestQuoteLqt), 0, 0, 0, 0]
                        }
                    }
                ],
                cpiIxs,
                cpiLuts,
                cpiSigners,
                loanAddress: loanExpanded?.loan.address,
                userPublicKey: activeWallet,
                setupIxs,
                setupSigners,
                isInitialWind: !loanExpanded, // If we have a loan, this is not the initial wind
                isLoop: true,
                analyticsInfo: createAnalyticsInfo<LoopAnalyticsInfo>({
                    transactionType: AnalyticsTransactionType.LoopWind,
                    loopIdentifier: loopExpanded.vaultIdentifier,
                    principalMint: principalMint,
                    collateralMint: loopExpanded.collateralToken.mint,
                    principalSymbol: loopExpanded.principalToken.symbol,
                    collateralSymbol: loopExpanded.collateralToken.symbol,
                    depositMint: inputMint,
                    slippagePercentDecimals: percentSlippageDecimals,
                    inputAmount: collateralAmountToDeposit,
                    outputAmount: finalSwapOutputLamports
                        ? lamportsToUiAmount(finalSwapOutputLamports, loopExpanded.collateralToken.decimals)
                        : undefined,
                    principalAmount: principalAmountRequested
                })
            };

            const windOnlyTx: AbfTransactionDetails<LoopWindParams> = {
                ...wind,
                sendOptions: {
                    ...wind.sendOptions
                }
            };

            const res = await send(windOnlyTx, params, {
                alerter: { generationEstimateSeconds: 8, showProgress: true, totalSteps: 1 },
                messageOverrides: {
                    errorMessageMap: WIND_ERRORS
                },
                mixpanelEvent: {
                    key: TrackTransactionEvent.SubmitEarnOrder,
                    params: trackSubmitEarnVault({ params })
                }
            });

            if (allTransactionsSucceeded(res)) {
                onSuccess?.(); // Form reset on success
            }
            return res;
        },
        [
            loopExpanded,
            activeWallet,
            externalQuote,
            multiplier,
            percentSlippageDecimals,
            loanCreationParams,
            send,
            wind,
            onSuccess,
            bestQuoteLendingStrategyAddress,
            bestQuoteApy,
            bestQuoteLqt,
            principalAmountRequested,
            principalMint,
            durationIndex,
            loanExpanded,
            collateralAmountToDeposit,
            getMetadata
        ]
    );
}

function useCalculateFairMarketCollateralAmounts() {
    const { loopExpanded } = useLoopContext();
    const {
        oraclePrices,
        externalQuote,
        form: { clmmPosition, inputToken, inputAmount },
        LoopWinder
    } = useWindContext();

    const collateralInputAmount = LoopWinder.getCollateralInputAmount({
        inputAmount,
        inputToken,
        loopExpanded,
        oraclePrices
    });

    return useMemo(() => {
        const principalInputAmount = externalQuote?.quote.inputAmount;
        if (!principalInputAmount || !loopExpanded) {
            return {
                totalFairMarketCollateralAmount: 0,
                swappedFairMarketCollateralOutAmount: 0
            };
        }

        const { principalToken } = loopExpanded;
        const principalOracle = oraclePrices?.get(principalToken.mint);

        // Convert principal amount to USD
        const principalInputAmountUi = lamportsToUiAmount(principalInputAmount, principalToken?.decimals);
        const principalInputAmountUsd = bsMath.mul(principalInputAmountUi, principalOracle?.marketPrice);

        const { collateralToken } = loopExpanded;
        const collateralOracle = oraclePrices?.get(collateralToken.mint);
        // Calculate fair market collateral amount from principal swap
        const swappedFairMarketCollateralOutAmount = bsMath.div(principalInputAmountUsd, collateralOracle?.marketPrice);

        const isFullInputSwapped =
            (isWhirlpoolLoop(loopExpanded) && !clmmPosition) ||
            isPrincipalTokenWind(loopExpanded.principalToken.mint, inputToken?.mint);

        // For full input swapped, only use swapped amount; otherwise add collateral input
        const totalFairMarketCollateralAmount = isFullInputSwapped
            ? swappedFairMarketCollateralOutAmount
            : bsMath.add(swappedFairMarketCollateralOutAmount, collateralInputAmount) ?? 0;

        return {
            totalFairMarketCollateralAmount,
            swappedFairMarketCollateralOutAmount
        };
    }, [externalQuote?.quote.inputAmount, oraclePrices, collateralInputAmount, clmmPosition, loopExpanded, inputToken]);
}

function useCalculateMarketCollateralAmounts() {
    const { externalQuote, quote, LoopWinder } = useWindContext();
    const { loopExpanded } = useLoopContext();

    return useMemo(() => {
        return LoopWinder.calculateMarketCollateralAmounts({
            loopExpanded,
            externalQuote,
            quote
        });
    }, [LoopWinder, externalQuote, quote, loopExpanded]);
}

export function useCalculateFairAndMarketValues() {
    const { loopExpanded } = useLoopContext();
    const { oraclePrices } = useWindContext();

    const { totalFairMarketCollateralAmount, swappedFairMarketCollateralOutAmount } =
        useCalculateFairMarketCollateralAmounts();

    const { totalMinOutputCollateralAmount, swappedMinOutputCollateralAmount } = useCalculateMarketCollateralAmounts();

    return useMemo(() => {
        const collateralOracle = oraclePrices?.get(loopExpanded?.collateralToken.mint ?? "");
        const collateralOraclePrice = collateralOracle?.usdPrice;
        const collateralMarketPrice = collateralOracle?.marketPrice ?? collateralOraclePrice;
        const swappedFairMarketValueUsd = bsMath.mul(swappedFairMarketCollateralOutAmount, collateralOraclePrice);

        const totalFairMarketValueUsd = bsMath.mul(totalFairMarketCollateralAmount, collateralOraclePrice);

        const totalReceivedMarketValueUsd = bsMath.mul(totalMinOutputCollateralAmount, collateralMarketPrice);

        const swappedReceivedMarketValueUsd = bsMath.mul(swappedMinOutputCollateralAmount, collateralMarketPrice);

        return {
            totalFairMarketValueUsd,
            totalReceivedMarketValueUsd,
            swappedFairMarketValueUsd,
            swappedReceivedMarketValueUsd
        };
    }, [
        totalFairMarketCollateralAmount,
        swappedFairMarketCollateralOutAmount,
        totalMinOutputCollateralAmount,
        swappedMinOutputCollateralAmount,
        oraclePrices,
        loopExpanded?.collateralToken.mint
    ]);
}

export function useWindSimulationBalanceChanges({
    loopExpanded,
    form,
    externalQuote,
    quote,
    leveragedCollateralAmountToDeposit,
    loopWinder,
    activeWallet,
    getMetadata,
    skip
}: WindSimulationBalanceChangesParams) {
    const wind = useLoopWindTransaction();

    const [simulationError, setSimulationError] = useState<WindSimulationError | undefined>(undefined);
    const lastSimulationKeyRef = useRef<string | undefined>(undefined);

    const simulationKey = useMemo(() => {
        if (
            !loopExpanded ||
            !externalQuote ||
            !quote ||
            !activeWallet ||
            !form.inputAmount ||
            !form.multiplier ||
            !form.inputToken?.mint ||
            skip
        ) {
            return undefined;
        }

        return [
            loopExpanded.vaultIdentifier,
            form.inputAmount,
            form.multiplier,
            form.percentSlippageDecimals ?? DEFAULT_SLIPPAGE,
            form.strategyDuration,
            form.inputToken?.mint,
            leveragedCollateralAmountToDeposit,
            externalQuote.quote.inputAmount,
            externalQuote.quote.minOutputAmount,
            quote.principalAmount,
            quote.bestQuote.lendingStrategyAddress,
            quote.bestQuote.apy,
            quote.bestQuote.liquidationThreshold
        ].join("|");
    }, [
        loopExpanded,
        externalQuote,
        quote,
        activeWallet,
        form.inputAmount,
        form.multiplier,
        form.percentSlippageDecimals,
        form.strategyDuration,
        form.inputToken?.mint,
        leveragedCollateralAmountToDeposit,
        skip
    ]);

    const simulate = useCallback(async () => {
        if (!simulationKey) {
            lastSimulationKeyRef.current = undefined;
            setSimulationError(undefined);
            return;
        }

        if (!loopExpanded || !externalQuote || !quote || !activeWallet || !form.inputToken?.mint) {
            return;
        }

        if (lastSimulationKeyRef.current === simulationKey) return;
        setSimulationError(undefined);
        lastSimulationKeyRef.current = simulationKey;
        const activeKey = simulationKey;
        const swapSteps = getSwapInstructionSteps(externalQuote);
        const [primarySwapStep, ...remainingSwapSteps] = swapSteps;
        const trailingSwapStep = mergeRouteInstructionSteps(remainingSwapSteps);

        if (trailingSwapStep) {
            // Multi-step routes are built separately; fall back to quote-based price impact.
            return;
        }

        const primarySwapInstructions = primarySwapStep ?? externalQuote?.swapInstructions?.[0];
        const targetCollateralMint = primarySwapStep?.outputMint ?? loopExpanded.collateralToken.mint;
        if (!primarySwapInstructions || !targetCollateralMint) {
            return;
        }

        if (quote.principalAmount === undefined || !quote.bestQuote.lendingStrategyAddress) {
            return;
        }

        const targetCollateralMetadata = getMetadata(targetCollateralMint) ?? loopExpanded.collateralToken;
        const assetData = getAssetData(targetCollateralMetadata, targetCollateralMint);
        if (!assetData) {
            return;
        }

        const { cpiIxs, cpiLuts, cpiSigners, setupIxs, setupSigners } = getCpiFromSwapStep(
            primarySwapInstructions,
            { flattenSetup: false }
        );

        const loanCreationParams = loopWinder.getLoanCreationParams({
            loopExpanded,
            form
        });

        const leverageMultiplier = (form.multiplier ?? 1) - (form.percentSlippageDecimals ?? DEFAULT_SLIPPAGE);

        const params: LoopWindParams = {
            loopExpanded,
            depositMint: form.inputToken.mint,
            depositCollateral: [
                {
                    collateralAmount: leveragedCollateralAmountToDeposit ?? 0,
                    collateralAssetData: assetData,
                    loanCreationParams
                }
            ],
            // Same as above: keep pandora's setupIxs separate so Jito auto-bundles setup + main.
            unifySetup: false,
            leverageMultiplier,
            principalRequested: [
                {
                    ledgerIndex: 0,
                    principalAmount: quote.principalAmount ?? 0,
                    principalMint: loopExpanded.principalToken.mint,
                    strategy: quote.bestQuote.lendingStrategyAddress ?? "",
                    durationIndex: getDurationIndex(form.strategyDuration)!,
                    expectedLoanValues: {
                        expectedApy: decimalsToBps(quote.bestQuote.apy),
                        expectedLqt: [decimalsToBps(quote.bestQuote.liquidationThreshold), 0, 0, 0, 0]
                    }
                }
            ],
            cpiIxs,
            cpiLuts,
            cpiSigners,
            loanAddress: undefined,
            userPublicKey: activeWallet,
            setupIxs,
            setupSigners,
            isInitialWind: true,
            isLoop: true,
            analyticsInfo: createAnalyticsInfo<LoopAnalyticsInfo>({
                transactionType: AnalyticsTransactionType.LoopWind,
                loopIdentifier: loopExpanded.vaultIdentifier,
                principalMint: loopExpanded.principalToken.mint,
                collateralMint: loopExpanded.collateralToken.mint,
                principalSymbol: loopExpanded.principalToken.symbol,
                collateralSymbol: loopExpanded.collateralToken.symbol,
                depositMint: form.inputToken.mint,
                slippagePercentDecimals: form.percentSlippageDecimals,
                inputAmount: form.inputAmount,
                outputAmount: externalQuote?.quote.minOutputAmount
                    ? lamportsToUiAmount(externalQuote.quote.minOutputAmount, loopExpanded.collateralToken.decimals)
                    : undefined,
                principalAmount: quote.principalAmount
            })
        };

        try {
            const result = await wind.getTransactionsWithParamsAndBalanceChanges(params);
            if (lastSimulationKeyRef.current !== activeKey) return;
            if (result.isErr()) {
                setSimulationError(undefined);
            } else {
                setSimulationError(parseWindSimulationError(result.unwrap().simulationError));
            }
        } catch (_error) {
            setSimulationError(undefined);
        }
    }, [
        simulationKey,
        loopExpanded,
        externalQuote,
        quote,
        activeWallet,
        form,
        leveragedCollateralAmountToDeposit,
        loopWinder,
        getMetadata,
        wind
    ]);

    useEffect(() => {
        void simulate();
    }, [simulate]);

    return simulationError;
}

/**
 * Floors to 0.1 precision and clamps to max leverage. Used in WindMultiplier and ManageLeverage for debounced multipliers.
 */
export function floorAndClamp(raw: number, maxLeverage: number): number {
    const floored = Math.floor(raw * 10) / 10;
    return Math.min(floored, Math.floor(maxLeverage * 10) / 10);
}

export function roundAndClamp(raw: number, maxLeverage: number): number {
    const rounded = Math.round(raw * 10) / 10;
    return Math.max(1, Math.min(rounded, Math.round(maxLeverage * 10) / 10));
}

export function useWindExecutionDetails({
    externalQuote,
    quote,
    form,
    loopExpanded,
    principalOracle,
    collateralOracle,
    labelOverrides
}: {
    externalQuote: SwapInstructionsResponse | undefined;
    quote: LoopQuoteExpanded | null | undefined;
    form: WindForm;
    loopExpanded: LoopExpanded | undefined;
    principalOracle: OracleQuote | undefined;
    collateralOracle: OracleQuote | undefined;
    labelOverrides?: Partial<ExecutionDetailsLabels>;
}) {
    const principalMarketPrice = principalOracle?.marketPrice;
    const collateralMarketPrice = collateralOracle?.marketPrice;
    if (
        !externalQuote ||
        !quote ||
        !loopExpanded ||
        principalMarketPrice === undefined ||
        collateralMarketPrice === undefined
    ) {
        return { executionDetails: undefined, receivedTokenValue: undefined };
    }

    const isPrincipalInput = isPrincipalTokenWind(loopExpanded.principalToken.mint, form.inputToken?.mint);
    const userPrincipalAmount = isPrincipalInput ? form.inputAmount ?? 0 : 0;
    const totalPrincipalAmount = bsMath.add(quote.principalAmount ?? 0, userPrincipalAmount) ?? 0;
    const userCollateralAmount = isPrincipalInput ? 0 : form.inputAmount ?? 0;

    const fairMarketCollateralAmount =
        (bsMath.div(bsMath.mul(totalPrincipalAmount, principalMarketPrice), collateralMarketPrice) ?? 0) +
        userCollateralAmount;
    const fairMarketValueUsd = bsMath.mul(fairMarketCollateralAmount, collateralMarketPrice) ?? 0;

    const swapOutAmount = lamportsToUiAmount(
        externalQuote.quote.minOutputAmount,
        loopExpanded.collateralToken.decimals
    );
    const initialCollateralAmount = isPrincipalInput ? 0 : quote.collateralAmount ?? form.inputAmount ?? 0;
    const positionCollateralAmount = bsMath.add(swapOutAmount, initialCollateralAmount) ?? 0;
    const receiveUsdAmount = bsMath.mul(positionCollateralAmount, collateralMarketPrice) ?? 0;

    const feeAmount = (externalQuote.quote.feeDecimals ?? 0) * receiveUsdAmount;
    const priceImpactAmount = externalQuote.quote.priceImpactDecimals
        ? externalQuote.quote.priceImpactDecimals * receiveUsdAmount
        : 0;

    const slippagePercentDecimals = form.percentSlippageDecimals;
    const slippageUsd = slippagePercentDecimals ? slippagePercentDecimals * fairMarketValueUsd : 0;
    const oraclePriceDeviationUsd = fairMarketValueUsd - receiveUsdAmount - feeAmount - priceImpactAmount - slippageUsd;

    const executionLossAmountUsd = bsMath.sub(receiveUsdAmount, fairMarketValueUsd) ?? 0;

    const q = externalQuote.quote;
    const p = loopExpanded.principalToken;
    const c = loopExpanded.collateralToken;
    let sellMeta: TokenListMetadata | undefined;
    let sellOracle: OracleQuote | undefined;
    if (q.inputMint === p.mint) {
        sellMeta = p;
        sellOracle = principalOracle;
    } else if (q.inputMint === c.mint) {
        sellMeta = c;
        sellOracle = collateralOracle;
    }
    let buyMeta: TokenListMetadata | undefined;
    if (q.outputMint === p.mint) {
        buyMeta = p;
    } else if (q.outputMint === c.mint) {
        buyMeta = c;
    }

    const executionPriceEstimate = getExecutionPriceUsdPerBoughtFromQuote({
        quote: q,
        sellMeta,
        buyMeta,
        sellUsdPrice: sellOracle?.usdPrice
    });

    // Percent of receive (decimal: 0.01 = 1%)
    const executionLossPercent = receiveUsdAmount ? executionLossAmountUsd / receiveUsdAmount : 0;
    const feePercent = receiveUsdAmount ? feeAmount / receiveUsdAmount : 0;
    const priceImpactPercent = receiveUsdAmount ? priceImpactAmount / receiveUsdAmount : 0;
    const slippagePercent = receiveUsdAmount ? slippageUsd / receiveUsdAmount : 0;
    const oraclePriceDeviationPercent = receiveUsdAmount ? oraclePriceDeviationUsd / receiveUsdAmount : 0;

    const executionDetails: LoopExecutionDetails = {
        labelOverrides,
        receiveUsdAmount: receiveUsdAmount,
        fairMarketValueUsd: fairMarketValueUsd,
        executionLossUsd: executionLossAmountUsd,
        executionLossPercent,
        feeUsd: feeAmount,
        feePercent,
        priceImpactUsd: priceImpactAmount,
        priceImpactPercent,
        slippageUsd,
        slippagePercent,
        oraclePriceDeviationUsd,
        oraclePriceDeviationPercent,
        executionPriceEstimate,
        externalQuote
    };

    return { executionDetails };
}
